import React, { useEffect, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { useTranslation } from 'react-i18next';
import { BiCopy, BiLogoTelegram, BiLogoWhatsapp, BiShare, BiSolidPlug, BiX } from 'react-icons/bi';
import { BsThreeDots } from 'react-icons/bs';
import { toast } from 'react-toastify';

import { useAppDispatch, useAppSelector } from '@/app/hook';
import { useGetScriptQuery } from '@/app/services/chatbot-rtk.services';
import { BannerHelper } from '@/components/common/BannerToast';
import { PSButton } from '@/components/ui/button';
import { Integrations } from '@/constants/Integrations.constant';
import { MultiLanguageUrl } from '@/constants/multiLanguage.constant';
import { WrappedErrorBoundary } from '@/pages/WrappedErrorBoundary';
import Utilities from '@/utils';
import { BotTypeEnum } from '@/utils/BotTypeEnum';

import { selectUseId } from '../auth/auth.slice';
import { botsUiState, handleToggleBotsIntegrationModal } from './botSlice';

const BotIntegrationModal = () => {
  const { t, i18n } = useTranslation();
  const dispatch = useAppDispatch();
  const { integrationModalData } = useAppSelector(state => state.bots.ui);
  const { showIntegrationModal } = useAppSelector(botsUiState);
  const [activeSection, setActiveSection] = useState(null);

  const userId = useAppSelector(selectUseId);
  const token = localStorage?.getItem(`LOUISEBOT_user_access_token`);

  const { data, isFetching, isError, isSuccess, refetch } = useGetScriptQuery(integrationModalData.botId, {
    skip: !integrationModalData.botId,
    refetchOnMountOrArgChange: true,
  });

  function getNewSessionCurlCommand(type: 'SHOW' | 'HIDE') {
    const tokenInCurl = type === 'SHOW' ? token : Utilities.renderHiddenToken(token as string);
    const newSessionCurlCommand = `curl --location '${import.meta.env.VITE_CHATBOT_WIDGET}/api/widget/headless/${userId}/${
      integrationModalData.botId
    }/new-session' \
--header 'content-type: application/json' \
--data '{
    "accessToken":"${tokenInCurl}" 
}'`;

    return newSessionCurlCommand;
  }

  const requestCurlCommand = `curl --location '${import.meta.env.VITE_CHATBOT_WIDGET}/api/widget/headless/${userId}/${
    integrationModalData.botId
  }/{{ session }}?active_stream=true' \
--header 'content-type: application/json' \
--data '{
    "request":"Hi there 👋 "
}'`;

  const isNonScenarioBot = !(integrationModalData?.botCode === BotTypeEnum.TEXT_SCENARIO || integrationModalData?.botCode === BotTypeEnum.VIDEO_SCENARIO);

  // Si c'est un bot de scénario, afficher automatiquement la section script
  useEffect(() => {
    if (!isNonScenarioBot && !activeSection) {
      setActiveSection('script');
    }
  }, [isNonScenarioBot, activeSection]);

  // Fonction pour générer les liens de partage
  const generateShareLinks = botId => {
    const message = `lb_${botId}`;
    const encodedMessage = encodeURIComponent(message);

    return {
      whatsapp: `https://wa.me/41783501747?text=${encodedMessage}`,
      telegram: `https://t.me/LB_AssistantBot?start=${encodedMessage}`,
    };
  };

  const shareLinks = generateShareLinks(integrationModalData.botId);

  const messagingPlatforms = [
    {
      name: 'Telegram',
      code: `lb_${integrationModalData.botId}`,
      icon: BiLogoTelegram,
      iconColor: 'blue',
      shareLink: shareLinks.telegram,
      isActive: true,
    },
    {
      name: 'WhatsApp',
      code: `lb_${integrationModalData.botId}`,
      icon: BiLogoWhatsapp,
      iconColor: 'green',
      shareLink: shareLinks.whatsapp,
      isActive: false,
    },
  ];

  const handleCopyShareLink = platform => {
    return t(`IntegrationsPage/toastCopySuccess`, { platform: platform });
  };

  const handleOpenShareLink = (platform, link) => {
    window.open(link, '_blank');
  };

  return (
    <Modal
      show={showIntegrationModal}
      centered
      onHide={() => dispatch(handleToggleBotsIntegrationModal({ status: false }))}
      className="use-assistant-modal p-0"
      size="lg"
    >
      <div className="tw-relative tw-w-full tw-h-full tw-flex lg:tw-flex-row tw-flex-col tw-overflow-hidden">
        <div className="tw-absolute tw-top-0 tw-right-0 tw-m-4 tw-cursor-pointer tw-z-10">
          <BiX
            onClick={() => {
              dispatch(handleToggleBotsIntegrationModal({ status: false }));
            }}
            className="tw-text-black tw-w-5 tw-h-5"
          />
        </div>
        <div className="tw-px-12 tw-py-8 tw-bg-gray-50 tw-rounded-tl-2xl tw-rounded-bl-2xl tw-flex-col tw-justify-center tw-items-center tw-gap-2.5 tw-inline-flex">
          <div className="tw-w-40 tw-h-40">
            <BiSolidPlug className="tw-w-full tw-h-full tw-text-gray-700" />
          </div>
        </div>
        <div className="lg:tw-pl-12 tw-pl-4 lg:tw-pr-3 tw-pr-4 tw-py-3 tw-bg-white tw-rounded-tr-2xl tw-rounded-br-2xl tw-justify-start tw-items-start tw-gap-8 tw-inline-flex tw-flex-1 tw-overflow-hidden">
          <div className="tw-grow tw-shrink tw-basis-0 tw-py-5 tw-flex-col tw-justify-start tw-items-start tw-gap-6 tw-inline-flex tw-overflow-auto tw-max-h-full">
            <div className="tw-self-stretch tw-flex-col tw-justify-start tw-items-start tw-gap-3 tw-flex">
              <div className="tw-justify-start tw-items-center tw-gap-2 tw-inline-flex">
                <div className="tw-text-gray-800 tw-text-3xl tw-font-semibold tw-font-['Lexend'] tw-leading-9">{t('IntegrationsPage/modal-title')}</div>
                <div className="tw-w-5 tw-h-5 tw-relative tw-origin-top-left tw-rotate-180 tw-overflow-hidden" />
              </div>
              <div className="tw-self-stretch tw-text-gray-500 tw-text-base tw-font-normal tw-font-['Lexend'] tw-leading-normal">
                {t('IntegrationsPage/modal-description')}
              </div>
            </div>

            {/* Bot Info Section */}
            <div className="tw-self-stretch tw-p-4 tw-bg-gray-100 tw-rounded-2xl tw-flex-col tw-justify-start tw-items-start tw-gap-4 tw-flex">
              <div className="tw-self-stretch tw-justify-between tw-items-start tw-flex tw-gap-2 tw-flex-col">
                <div className="lg:tw-w-2/4 tw-w-full tw-flex-col tw-justify-start tw-items-start tw-gap-2 tw-inline-flex tw-min-w-0">
                  <div className="tw-text-gray-800 tw-text-lg tw-font-semibold tw-font-['Lexend'] tw-leading-7 tw-break-words">
                    {integrationModalData?.title}
                  </div>
                  <div className="tw-text-gray-500 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight">
                    {(() => {
                      switch (integrationModalData?.botCode) {
                        case BotTypeEnum.TEXT_SCENARIO:
                          return t('IntegrationsPage/textScenario');
                        case BotTypeEnum.VIDEO_SCENARIO:
                          return t('IntegrationsPage/videoScenario');
                        default:
                          return t('IntegrationsPage/ai');
                      }
                    })()}
                  </div>
                </div>

                {/* Integration Options Buttons - Only show for non-scenario bots */}
                {isNonScenarioBot && (
                  <div className="lg:tw-justify-end tw-items-center tw-gap-3 tw-flex tw-flex-col sm:tw-flex-row tw-w-full lg:tw-mt-0 tw-mt-4">
                    <PSButton
                      id="btn-integration-script"
                      variant={activeSection === 'script' ? 'primary' : 'secondary'}
                      className="gap-2 tw-flex-shrink-0 tw-w-full sm:tw-w-auto"
                      onClick={() => setActiveSection(activeSection === 'script' ? null : 'script')}
                    >
                      <div className="tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight tw-whitespace-nowrap">
                        {t('IntegrationsPage/scriptIntegration')}
                      </div>
                    </PSButton>

                    <PSButton
                      id="btn-integration-api"
                      variant={activeSection === 'api' ? 'primary' : 'secondary'}
                      className="gap-2 tw-flex-shrink-0 tw-w-full sm:tw-w-auto"
                      onClick={() => setActiveSection(activeSection === 'api' ? null : 'api')}
                    >
                      <div className="tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight tw-whitespace-nowrap">
                        {t('IntegrationsPage/apiIntegration')}
                      </div>
                    </PSButton>

                    <PSButton
                      id="btn-integration-messaging"
                      variant={activeSection === 'messaging' ? 'primary' : 'secondary'}
                      className="gap-2 tw-flex-shrink-0 tw-w-full sm:tw-w-auto"
                      onClick={() => setActiveSection(activeSection === 'messaging' ? null : 'messaging')}
                    >
                      <div className="tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight tw-whitespace-nowrap">
                        {t('IntegrationsPage/messagingPlatforms')}
                      </div>
                    </PSButton>
                  </div>
                )}
              </div>

              {/* Content Sections */}
              {activeSection === 'script' && (
                <div className="tw-self-stretch tw-flex-col tw-justify-start tw-items-start tw-gap-3 tw-flex">
                  <BannerHelper message={t('HelpBanner/Integration/script/hint')} />

                  <div className="tw-flex tw-gap-2 tw-items-center">
                    <CopyToClipboard text={isSuccess ? data?.script : ''} onCopy={() => toast.success(t('IntegrationsPage/toastCopyScriptSuccess'))}>
                      <PSButton
                        id="btn-integration-copy"
                        size="sm"
                        variant="secondary"
                        className="tw-flex tw-items-center tw-gap-1"
                        onClick={isError ? refetch : undefined}
                        isLoading={isFetching}
                      >
                        <BiCopy className="tw-w-4 tw-h-4" />
                        {t('WidgetContent/btn-02')}
                      </PSButton>
                    </CopyToClipboard>
                  </div>
                  <div
                    translate="no"
                    style={{ wordBreak: 'break-word' }}
                    className="tw-w-full tw-text-gray-800 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight tw-pl-4 tw-pr-3 tw-py-2 tw-bg-white tw-rounded-md tw-shadow-sm tw-border tw-border-gray-300"
                  >
                    {data?.script}
                  </div>
                </div>
              )}

              {activeSection === 'api' && (
                <div
                  style={{ wordBreak: 'break-word' }}
                  className="tw-w-full tw-text-gray-800 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight tw-pl-4 tw-pr-3 tw-py-2 tw-bg-white tw-rounded-md tw-shadow-sm tw-border tw-border-gray-300 tw-flex-col tw-justify-start tw-items-start tw-gap-3 tw-inline-flex"
                >
                  <BannerHelper message={t('HelpBanner/Integration/api/hint')} />
                  <span>
                    <u>{t('WidgetContent/l-title')}</u>
                  </span>
                  <CopyToClipboard text={userId} onCopy={() => toast.success(t('WidgetContent/pop-04'))}>
                    <span className="tw-cursor-pointer tw-break-all">
                      {t('WidgetContent/l-01')}
                      {userId}
                      <span className="tw-ml-2">
                        <BiCopy color="grey" />
                      </span>
                    </span>
                  </CopyToClipboard>

                  <CopyToClipboard text={integrationModalData.botId} onCopy={() => toast.success(t('WidgetContent/pop-05'))}>
                    <span className="tw-cursor-pointer tw-break-all">
                      {t('WidgetContent/l-02')}
                      <span>{integrationModalData?.botId}</span>
                      <span className="tw-ml-2">
                        <BiCopy color="grey" />
                      </span>
                    </span>
                  </CopyToClipboard>

                  <CopyToClipboard text={token as string} onCopy={() => toast.success(t('WidgetContent/pop-06'))}>
                    <span className="tw-cursor-pointer tw-break-all">
                      {t('WidgetContent/l-03')} {Utilities.renderHiddenToken(token as string)}
                      <span className="tw-ml-2">
                        <BiCopy color="grey" />
                      </span>
                    </span>
                  </CopyToClipboard>
                  <hr className="tw-w-full" />
                  <CopyToClipboard text={getNewSessionCurlCommand('SHOW')} onCopy={() => toast.success(t('WidgetContent/pop-07'))}>
                    <span className="tw-cursor-pointer">
                      <span>
                        <u>{t('WidgetContent/l-04')}</u>
                        <span className="tw-ml-2">
                          <BiCopy color="grey" />
                        </span>
                      </span>
                      <pre className="tw-whitespace-pre-wrap tw-mt-3 tw-overflow-auto">
                        <code style={{ wordBreak: 'break-word', fontSize: '12px' }}>{getNewSessionCurlCommand('HIDE')}</code>
                      </pre>
                    </span>
                  </CopyToClipboard>

                  <hr className="tw-w-full" />
                  <CopyToClipboard text={requestCurlCommand} onCopy={() => toast.success(t('WidgetContent/pop-07'))}>
                    <span className="tw-cursor-pointer">
                      <span>
                        <u>{t('WidgetContent/l-05')}</u>
                        <span className="tw-ml-2">
                          <BiCopy color="grey" />
                        </span>
                      </span>
                      <pre className="tw-whitespace-pre-wrap tw-mt-3 tw-overflow-auto">
                        <code style={{ wordBreak: 'break-word', fontSize: '12px' }}>{requestCurlCommand}</code>
                      </pre>
                    </span>
                  </CopyToClipboard>
                </div>
              )}

              {activeSection === 'messaging' && (
                <div className="tw-self-stretch tw-flex-col tw-justify-start tw-items-start tw-gap-3 tw-flex">
                  <BannerHelper message={t('HelpBanner/Integration/messaging/hint')} />

                  <div className="tw-text-sm">
                    <p>{t('IntegrationsPage/messagingExplanation/line1')}</p>
                    <p>{t('IntegrationsPage/messagingExplanation/line2')}</p>
                    <p>{t('IntegrationsPage/messagingExplanation/line3')}</p>
                  </div>
                  <div className="tw-flex tw-flex-col tw-gap-3 tw-w-full">
                    {messagingPlatforms.map(platform => (
                      <div
                        key={platform.name}
                        className={`tw-flex tw-flex-col tw-gap-2 tw-p-3 tw-bg-white tw-rounded-md tw-shadow-sm tw-border tw-border-gray-300 ${
                          !platform.isActive ? 'tw-opacity-60' : ''
                        }`}
                      >
                        {/* Platform Header */}
                        <div className="tw-flex tw-items-center tw-justify-between">
                          <div className="tw-flex tw-items-center tw-gap-3">
                            <span className="tw-text-lg">
                              <platform.icon color={platform.isActive ? platform.iconColor : 'gray'} size={24} />
                            </span>
                            <div className="tw-flex tw-flex-col">
                              <span className={`tw-text-sm tw-font-medium ${platform.isActive ? 'tw-text-gray-800' : 'tw-text-gray-400'}`}>
                                {platform.name}
                              </span>
                            </div>
                          </div>
                          {!platform.isActive && (
                            <span className="tw-text-xs tw-font-medium tw-text-amber-600 tw-bg-amber-50 tw-px-2 tw-py-1 tw-rounded-full">
                              {t('IntegrationsPage/comingSoon')}
                            </span>
                          )}
                        </div>

                        {/* Share Link Section */}
                        <div className="tw-flex tw-items-center tw-justify-between tw-pt-2 tw-border-t tw-border-gray-200">
                          {platform.isActive ? (
                            <div className="tw-flex tw-gap-2 tw-flex-wrap">
                              <PSButton
                                id="btn-integration-copy"
                                size="sm"
                                variant="outline"
                                className="tw-flex tw-items-center tw-gap-1"
                                onClick={() => handleOpenShareLink(platform.name, platform.shareLink)}
                              >
                                <BiShare className="tw-w-4 tw-h-4" />
                                {t('IntegrationsPage/test')}
                              </PSButton>
                              <CopyToClipboard text={isSuccess ? platform.shareLink : ''} onCopy={() => toast.success(handleCopyShareLink(platform.name))}>
                                <PSButton id="btn-integration-share" size="sm" variant="secondary" className="tw-flex tw-items-center tw-gap-1">
                                  <BiCopy className="tw-w-4 tw-h-4" />
                                  {t('IntegrationsPage/readyToShare')}
                                </PSButton>
                              </CopyToClipboard>
                            </div>
                          ) : (
                            <div className="tw-text-sm tw-text-gray-400 tw-italic">{t('IntegrationsPage/workingOnIt')}</div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Third Party Integrations Section - Always show when script is active */}
            {activeSection === 'script' && (
              <div className="tw-self-stretch tw-flex-col tw-justify-center tw-items-start tw-gap-4 tw-flex">
                <div className="tw-text-gray-800 tw-text-lg tw-font-semibold tw-font-['Lexend'] tw-leading-7">{t('IntegrationsPage/modal-subTitle')}</div>
                <div className="tw-justify-start tw-items-start tw-gap-4 tw-inline-flex lg:tw-flex-nowrap tw-flex-wrap">
                  {Integrations.slice(0, 4).map(item => (
                    <div
                      role="button"
                      tabIndex={0}
                      className="tw-w-20 tw-h-24 tw-relative tw-cursor-pointer"
                      key={item.name}
                      onClick={() => {
                        window.open(`${import.meta.env.VITE_UI_ADDRESS}/${MultiLanguageUrl[i18n.language] || 'en-US'}/chatbot-integration`, '_blank');
                        dispatch(handleToggleBotsIntegrationModal({ status: false }));
                      }}
                      onKeyDown={e => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          window.open(`${import.meta.env.VITE_UI_ADDRESS}/${MultiLanguageUrl[i18n.language] || 'en-US'}/chatbot-integration`, '_blank');
                          dispatch(handleToggleBotsIntegrationModal({ status: false }));
                        }
                      }}
                    >
                      <div className="tw-w-20 tw-h-20 tw-left-0 tw-top-0 tw-absolute">
                        <img src={item.image} alt={item.name} className="tw-w-20 tw-h-20 tw-left-0 tw-top-0 tw-absolute tw-bg-gray-100 tw-rounded-2xl" />
                      </div>
                      <div className="tw-w-16 tw-left-[8px] tw-top-[88px] tw-absolute tw-text-center tw-text-gray-500 tw-text-xs tw-font-normal tw-font-['Lexend'] tw-leading-none">
                        {item.name}
                      </div>
                    </div>
                  ))}
                  <div className="tw-w-20 tw-h-24 tw-relative tw-cursor-pointer">
                    <div
                      onClick={() => {
                        window.open(`${import.meta.env.VITE_UI_ADDRESS}/${MultiLanguageUrl[i18n.language] || 'en-US'}/chatbot-integration`, '_blank');
                        dispatch(handleToggleBotsIntegrationModal({ status: false }));
                      }}
                      onKeyDown={e => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          window.open(`${import.meta.env.VITE_UI_ADDRESS}/${MultiLanguageUrl[i18n.language] || 'en-US'}/chatbot-integration`, '_blank');
                          dispatch(handleToggleBotsIntegrationModal({ status: false }));
                        }
                      }}
                      role="button"
                      id="btn-integration-more"
                      aria-label="more integrations"
                      tabIndex={0}
                      className="tw-cursor-pointer tw-h-20 tw-bg-gray-100 tw-rounded-xl tw-flex tw-items-center tw-justify-center tw-p-2"
                    >
                      <BsThreeDots color="gray" size={24} />
                    </div>
                    <div className="tw-text-center tw-mt-2 tw-text-zinc-500 tw-text-xs tw-font-normal font-sofiaSans tw-leading-none">
                      {t('DashboardSettingCustomizeChat/other')}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default WrappedErrorBoundary(BotIntegrationModal);
